(() => {
  if (customElements.get('sf8-buyback')) return;
  const escape = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character],
    );
  const wearValues = ['none', 'light', 'heavy', 'damaged'];
  const functionValues = ['yes', 'no', 'unknown'];
  customElements.define(
    'sf8-buyback',
    class extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        this.config = JSON.parse(this.querySelector('[data-buyback-config]').textContent);
        this.labels = { ...Object.assign({}, ...(this.config.labelGroups || [])), ...this.config.labels };
        this.models = (this.config.models || []).filter(
          (model) => model.brand && model.name && Array.isArray(model.storage),
        );
        if (typeof this.config.colourTints === 'string') {
          try {
            this.config.colourTints = JSON.parse(this.config.colourTints);
          } catch {
            this.config.colourTints = {};
          }
        }
        this.locations = (this.config.locations || []).map((place) =>
          typeof place === 'string' ? { name: place, address: '' } : place,
        );
        this.brands = [...new Set(this.models.map((model) => model.brand))];
        this.key = `sf-sell-v3:${this.dataset.sectionId || 'preview'}`;
        this.answers = {};
        this.position = 0;
        this.form = this.querySelector('[data-sell-form]');
        const opts = { signal: this.abort.signal };
        if (this.querySelector('[data-sell-success]')) {
          try {
            sessionStorage.removeItem(this.key);
          } catch {}
          this.renderPhotoGuides(this.querySelector('[data-success-guides]'));
          this.bindHelp(opts);
          return;
        }
        if (!this.form) return;
        this.restore();
        this.questions = this.buildQuestions();
        this.renderShell();
        this.previewObserver = new IntersectionObserver(
          (entries) => this.classList.toggle('is-scrolled', !entries[0]?.isIntersecting),
          { rootMargin: '-90px 0px 0px 0px' },
        );
        this.previewObserver.observe(this.querySelector('[data-sell-progress]'));
        this.bindHelp(opts);
        this.form.addEventListener('change', (event) => this.change(event), opts);
        this.form.addEventListener('input', (event) => this.input(event), opts);
        this.form.addEventListener('submit', (event) => this.submit(event), opts);
        // Photos of the device: chosen per slot, shrunk in the browser, sent with the request.
        this.addEventListener(
          'change',
          (event) => {
            const input = event.target.closest('[data-photo-slot]');
            if (input) this.addPhoto(input);
          },
          opts,
        );
        this.addEventListener(
          'click',
          (event) => {
            const remove = event.target.closest('[data-photo-remove]');
            if (!remove) return;
            delete this.photos[remove.dataset.photoRemove];
            this.show(this.position, true, false);
          },
          opts,
        );
        this.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('[data-sell-preview-submit]')) this.submit(event);
            const edit = event.target.closest('[data-sell-edit]');
            if (edit) this.show(Number(edit.dataset.sellEdit), event.detail === 0);
            const check = event.target.closest('[data-function-cycle]');
            if (check) this.cycleCheck(check, event.detail === 0);
            if (event.target.closest('[data-sell-back]')) this.show(this.position - 1, event.detail === 0);
            if (event.target.closest('[data-sell-next]') && this.validate())
              this.show(this.position + 1, event.detail === 0);
          },
          opts,
        );
        this.position = Math.max(0, Math.min(this.position, this.questions.length - 1));
        if (this.querySelector('[data-native-errors]')) this.position = this.questions.length - 1;
        // A partially answered saved device must resume at its first missing required field.
        const missing = this.questions.findIndex((question) => !this.valid(question));
        if (missing >= 0 && this.position > missing) this.position = missing;
        this.show(this.position, true, false);
      }
      t(key) {
        return this.labels[key] || '';
      }
      buildQuestions() {
        const question = (key, type) => {
          const step = this.config.steps?.find((entry) => entry.key === key);
          return {
            key,
            type,
            heading: step?.heading || this.t(key),
            summaryLabel: step?.summary_label || this.t('summary_' + key),
            text: step?.text || '',
            guide: key === 'location' ? 'handover' : key,
          };
        };
        return [
          question('brand', 'brand'),
          question('model', 'model'),
          question('storage', 'storage'),
          question('colour', 'colour'),
          question('battery', 'battery'),
          question('functions', 'functions'),
          question('screenFaults', 'faults'),
          ...['display', 'frame', 'back'].map((key) => question(key, 'wear')),
          question('location', 'location'),
          question('summary', 'summary'),
        ];
      }
      model() {
        return this.models.find((model) => model.name === this.answers.model && model.brand === this.answers.brand);
      }
      restore() {
        try {
          const saved = JSON.parse(sessionStorage.getItem(this.key));
          if (saved?.version !== 3 || typeof saved.answers !== 'object' || !saved.answers) return;
          const source = saved.answers;
          for (const [key, value] of Object.entries(source)) {
            if (typeof value === 'string' && value.length <= 254) this.answers[key] = value;
            if (key === 'screenFaults' && Array.isArray(value))
              this.answers[key] = value.filter((entry) =>
                ['spots', 'pixels', 'lines', 'touch', 'none', 'unknown'].includes(entry),
              );
          }
          if (!this.brands.includes(this.answers.brand)) delete this.answers.brand;
          if (!this.model()) {
            delete this.answers.model;
            delete this.answers.storage;
          }
          if (!this.model()?.storage.includes(this.answers.storage)) delete this.answers.storage;
          if (!this.locations.some((place) => place.name === this.answers.location)) delete this.answers.location;
          for (const key of ['display', 'frame', 'back'])
            if (!wearValues.includes(this.answers[key])) delete this.answers[key];
          this.position = Number.isInteger(saved.position) ? saved.position : 0;
        } catch {}
      }
      save() {
        try {
          sessionStorage.setItem(
            this.key,
            JSON.stringify({ version: 3, position: this.position, answers: this.answers }),
          );
        } catch {}
      }
      renderShell() {
        this.form.querySelector('[data-sell-content]').innerHTML = `
          <div class="sf10-sell-top">
            <span data-sell-count></span>
            <span>${escape(this.config.reply)}</span>
          </div>
          <progress class="sf10-progress" data-sell-progress max="${this.questions.length}" value="1"
            aria-label="${escape(this.t('progress'))}"></progress>
          <div class="sf10-sell-layout">
            <aside class="sf10-device-card">
              <div class="sf10-device-caption">
                <span>${escape(this.t('your_device'))}</span>
                <strong data-device-model></strong>
              </div>
              ${this.phone()}
              <div class="sf10-device-facts" data-device-facts role="status" aria-live="polite"></div>
            </aside>
            <div class="sf10-sell-question">
              <section data-sell-question></section>
              <p data-sell-validation role="status" class="sf9-validation"></p>
              <div class="sf10-sell-nav">
                <button type="button" data-sell-back>${escape(this.t('previous'))}</button>
                <button type="button" data-sell-next class="sf8-primary">
                  ${escape(this.t('continue'))}${this.lineIcon('arrow')}
                </button>
              </div>
            </div>
          </div>
          <div data-answer-fields></div>`;
      }
      lineIcon(key, className = '') {
        const paths = {
          power: '<path d="M12 3v8M6.5 5.8a8 8 0 1 0 11 0"/>',
          network:
            '<path d="M3 8a14 14 0 0 1 18 0M6 12a9 9 0 0 1 12 0M9 16a4 4 0 0 1 6 0"/><circle cx="12" cy="20" r=".75"/>',
          biometrics:
            '<path d="M8 3H5a2 2 0 0 0-2 2v3m13-5h3a2 2 0 0 1 2 2v3M3 16v3a2 2 0 0 0 2 2h3m8 0h3a2 2 0 0 0 2-2v-3M8 9v2m8-2v2m-4-2v5h-2m-2 3a6 6 0 0 0 8 0"/>',
          camera:
            '<rect x="3" y="7" width="18" height="13" rx="3"/><path d="m8 7 2-3h4l2 3"/><circle cx="12" cy="13.5" r="3.5"/>',
          audio: '<path d="M4 10v4h4l5 4V6l-5 4ZM17 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14"/>',
          charging: '<path d="m13 3-8 11h6l-1 7 9-12h-6Z"/>',
          buttons: '<path d="M4 7h3m4 0h9M4 17h9m4 0h3"/><circle cx="9" cy="7" r="2"/><circle cx="15" cy="17" r="2"/>',
          liquid: '<path d="M12 3C10 7 5 11 5 15a7 7 0 0 0 14 0c0-4-5-8-7-12Z"/><path d="M12 11v4m0 3h.01"/>',
          battery: '<rect x="3" y="6" width="17" height="12" rx="2"/><path d="M22 10v4M6 9v6m4-6v6m4-6v6"/>',
          screen: '<rect x="6" y="2.5" width="12" height="19" rx="3"/><path d="M10 5h4m-3 13h2"/>',
          check: '<path d="m5 12 4 4L19 6"/>',
          cross: '<path d="m6 6 12 12M18 6 6 18"/>',
          help: '<circle cx="12" cy="12" r="9"/><path d="M9.5 8.5a2.6 2.6 0 1 1 4.3 2c-1.1.8-1.8 1.2-1.8 2.5m0 3h.01"/>',
          info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v6m0-10h.01"/>',
          warning:
            '<path d="m10.3 4-8 14a2 2 0 0 0 1.7 3h16a2 2 0 0 0 1.7-3l-8-14a2 2 0 0 0-3.4 0Z"/><path d="M12 9v5m0 3h.01"/>',
          empty: '<circle cx="12" cy="12" r="8"/>',
          plus: '<path d="M12 5v14M5 12h14"/>',
          home: '<path d="m3 10 9-7 9 7M5 9v12h14V9m-10 12v-7h6v7"/>',
          package: '<path d="m3 7 9-4 9 4v12l-9 3-9-3Zm0 0 9 4 9-4m-9 4v11M7.5 5l9 4"/>',
          arrow: '<path d="M4 12h16m-6-6 6 6-6 6"/>',
          close: '<path d="m6 6 12 12M18 6 6 18"/>',
        };
        return `<svg class="sf12-icon ${className}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[key] || paths.screen}</svg>`;
      }
      stateIcon(check) {
        const state = this.checkState(check);
        return this.lineIcon(
          { works: 'check', issue: check.negative ? 'warning' : 'cross', unknown: 'help', unset: 'empty' }[state],
        );
      }
      phone() {
        const uid = `sell-device-${this.dataset.sectionId || 'preview'}-${(this.artCount = (this.artCount || 0) + 1)}`;
        const model = this.answers.model || '';
        const battery = this.answers.batteryUnknown !== 'yes' ? Number(this.answers.batteryPercent) || 0 : 0;
        const words = model.split(' ');
        const firstLine = words.slice(0, 2).join(' ');
        const secondLine = words.slice(2).join(' ');
        const rings = {
          power: ['<rect x="247" y="151" width="7" height="48" rx="3.5"/>', 234, 143],
          network: ['<circle cx="215" cy="67" r="10"/>', 207, 85],
          biometrics: ['<ellipse cx="147" cy="47" rx="15" ry="9"/>', 154, 71],
          camera: ['<circle cx="173" cy="48" r="8"/>', 198, 69],
          audio: ['<rect x="134" y="27" width="34" height="5" rx="2.5"/>', 204, 24],
          charging: ['<rect x="137" y="449" width="32" height="7" rx="3.5"/>', 152, 475],
          buttons: ['<rect x="48" y="126" width="7" height="58" rx="3.5"/>', 79, 120],
          liquid: ['<rect x="52" y="26" width="196" height="424" rx="36"/>', 104, 431],
        };
        return `<div class="sf10-device-stage" aria-hidden="true"><div class="sf10-device-body" style="--device-tint:${this.tint(this.answers.colour)}">
          <svg class="sf12-device-svg" viewBox="0 0 292 490" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="${uid}-back" x1="0" y1="0" x2="1" y2="1"><stop stop-color="var(--device-tint)"/><stop offset="1" stop-color="var(--device-tint)" stop-opacity=".72"/></linearGradient>
              <linearGradient id="${uid}-frame" x1="0" y1="0" x2="1" y2="0"><stop stop-color="#b9bec3"/><stop offset=".1" stop-color="var(--device-tint)"/><stop offset=".55" stop-color="#dce0e3"/><stop offset="1" stop-color="var(--device-tint)"/></linearGradient>
              <linearGradient id="${uid}-glass" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#f8f9fa"/><stop offset="1" stop-color="#e9edf0"/></linearGradient>
              <filter id="${uid}-shadow" x="-30%" y="-10%" width="170%" height="140%"><feDropShadow dx="0" dy="10" stdDeviation="9" flood-color="#17202a" flood-opacity=".15"/></filter>
              <clipPath id="${uid}-screen"><rect x="61" y="34" width="180" height="407" rx="27"/></clipPath>
            </defs>
            <g filter="url(#${uid}-shadow)">
              <rect class="sf10-device-back" x="40" y="38" width="195" height="422" rx="36" fill="url(#${uid}-back)" stroke="#9ea5ad" stroke-width="1.5"/>
              <rect x="41.5" y="39.5" width="192" height="419" rx="34.5" stroke="#fff" stroke-opacity=".5"/>
              <rect class="sf10-device-frame" x="52" y="26" width="196" height="424" rx="36" fill="url(#${uid}-frame)" stroke="#727980" stroke-width="1.5"/>
              <rect x="55" y="29" width="190" height="418" rx="33" fill="#20242a"/>
              <rect class="sf10-device-screen" x="61" y="34" width="180" height="407" rx="27" fill="url(#${uid}-glass)"/>
              <rect x="121" y="40" width="60" height="16" rx="8" fill="#15181c"/>
              <circle cx="173" cy="48" r="3" fill="#253747"/><circle cx="173" cy="48" r="1.4" fill="#465967"/><rect x="134" y="29" width="34" height="2" rx="1" fill="#49515a"/>
              <path d="M53 94v17m0 18v24m0 14v24m194-39v39" stroke="#7f8791" stroke-width="3" stroke-linecap="round"/>
              <g clip-path="url(#${uid}-screen)" font-family="inherit">
                <rect x="73" y="87" width="156" height="119" rx="13" fill="#fff" stroke="#dde2e7" stroke-width=".8"/>
                <text x="85" y="109" class="sf12-device-model" data-device-screen-model>${escape(firstLine)}</text>
                <text x="85" y="125" class="sf12-device-model" data-device-screen-model-rest>${escape(secondLine)}</text>
                <text x="85" y="147" class="sf12-device-meta" data-device-storage>${escape(this.answers.storage ? this.answers.storage + ' GB' : '—')}</text>
                <path d="M85 157h132" stroke="#e5e9ed"/>
                <text x="85" y="175" class="sf12-device-label">${escape(this.t('battery_short'))}</text>
                <text x="217" y="175" text-anchor="end" class="sf12-device-meta" data-device-battery>${battery ? battery + '%' : '—'}</text>
                <rect x="85" y="186" width="132" height="6" rx="3" fill="#e5e9ed"/>
                <rect x="85" y="186" width="${battery * 1.32}" height="6" rx="3" fill="var(--j-accent)" data-device-battery-fill/>
                <g data-device-checks></g>
                <g class="sf12-preview-defect" data-preview-wear="display"><path d="m65 220 34-27-9 42 28-10-17 31"/></g>
              </g>
              <rect x="128" y="427" width="46" height="3" rx="1.5" fill="#adb5bd"/>
            </g>
            <g class="sf12-function-rings" stroke="var(--j-accent)" stroke-width="1.5" stroke-linecap="round">
              ${(this.config.checks || [])
                .map((check) => {
                  const [shape, x, y] = rings[check.guide || check.key] || rings.power;
                  return `<g data-device-part="${escape(check.guide || check.key)}" opacity="0">${shape}<text x="${x}" y="${y}" text-anchor="middle" class="sf12-ring-label" stroke="none" fill="currentColor">${escape(check.short_label || check.heading)}</text></g>`;
                })
                .join('')}
            </g>
          </svg></div></div>`;
      }
      conditionArt(side, severity = 'none', fault = '') {
        const uid = `sell-condition-${(this.artCount = (this.artCount || 0) + 1)}`;
        const marks = {
          light: '<path d="m53 54 9-9m-4 16 5-5m-14 46 8-8"/>',
          heavy: '<path d="m40 39 13-12m-8 39 21-21m-26 30 19-19m-22 53 20-20m-16 26 21-21"/>',
          damaged: '<path d="m28 28 20 25-8 13 28 12-7 24 13 13M48 53l20-10m-7 59-26 15"/>',
        };
        const faults = {
          spots: '<circle cx="60" cy="63" r="12" fill="#fff" stroke="#c8cfd6"/>',
          pixels: '<path d="M45 52h3v3h-3zm18 29h3v3h-3zm-8 18h3v3h-3z" fill="#57616b" stroke="none"/>',
          lines: '<path d="M60 29v94M29 88h62" stroke="var(--j-accent)"/>',
          touch: '<circle cx="59" cy="80" r="13"/><path d="m50 80 18 0m-9-9v18"/>',
          none: '<path d="m47 77 9 9 18-22"/>',
          unknown: '<circle cx="60" cy="79" r="16"/><path d="M55 73a5 5 0 1 1 8 4c-2 1-3 3-3 5m0 5h.01"/>',
        };
        const frame = side === 'frame';
        return `<svg class="sf12-condition-svg" viewBox="0 0 120 150" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <defs><clipPath id="${uid}-clip"><rect x="${frame ? 55 : 33}" y="20" width="${frame ? 10 : 54}" height="117" rx="${frame ? 4 : 11}"/></clipPath><linearGradient id="${uid}" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#f8f9fa"/><stop offset="1" stop-color="#e8ecef"/></linearGradient></defs>
          <rect x="${frame ? 55 : 33}" y="20" width="${frame ? 10 : 54}" height="${117}" rx="${frame ? 4 : 11}" fill="url(#${uid})" stroke="#929ca6"/>
          ${frame ? '<path d="M56 38v14m0 8v19M64 53v23M58 126h4" stroke="#8b949d"/>' : side === 'back' ? '<rect x="39" y="25" width="20" height="24" rx="6" stroke="#a1a9b1"/><circle cx="41" cy="35" r="4"/><circle cx="49" cy="45" r="4"/>' : '<rect x="36" y="20" width="48" height="111" rx="8" stroke="#bcc4cc"/><rect x="50" y="24" width="20" height="5" rx="2.5" fill="#4b535c" stroke="none"/>'}
          <g stroke="var(--j-accent)" clip-path="url(#${uid}-clip)">${faults[fault] || (frame ? { light: '<path d="m57 96 4-8"/>', heavy: '<path d="m57 96 4-8m-4 21 6-11m10-7 4-7"/>', damaged: '<path d="m56 99 6-14-2 21 4-5-1 20"/>' }[severity] : marks[severity]) || ''}</g>
        </svg>`;
      }
      radio(key, value, text, visual = '') {
        return `<label class="sf10-answer-card"><input type="radio" name="sell-${escape(key)}" data-sell-field="${escape(key)}" value="${escape(value)}" ${this.answers[key] === value ? 'checked' : ''}>${visual}<span>${escape(text)}</span><i aria-hidden="true">${this.lineIcon('check')}</i></label>`;
      }
      show(position, instant = false, focus = true) {
        clearTimeout(this.advanceTimer);
        this.position = Math.max(0, Math.min(position, this.questions.length - 1));
        this.classList.toggle('has-started', this.position > 0);
        const question = this.questions[this.position];
        this.dataset.questionType = question.type;
        this.classList.toggle('instant', instant || matchMedia('(prefers-reduced-motion: reduce)').matches);
        this.querySelector('[data-sell-count]').textContent = `${this.position + 1} / ${this.questions.length}`;
        this.querySelector('[data-sell-progress]').value = this.position + 1;
        const contents = this.content(question);
        this.querySelector('[data-sell-question]').innerHTML =
          `<h2 tabindex="-1" data-question-heading>${escape(question.heading)}</h2>${question.text ? `<p class="sf10-question-intro">${escape(question.text)}</p>` : ''}${contents}${!['summary', 'brand', 'colour', 'functions'].includes(question.type) ? `<button type="button" class="sf9-help-link" data-guide-button="${escape(question.guide)}">${escape(this.t('how_check'))} ${this.lineIcon('arrow')}</button>` : ''}`;
        this.querySelector('[data-sell-back]').disabled = this.position === 0;
        this.querySelector('[data-sell-next]').hidden = question.type === 'summary';
        this.querySelector('[data-sell-validation]').textContent = '';
        this.updatePreview();
        this.syncFields();
        this.save();
        if (focus) {
          const heading = this.querySelector('[data-sell-question] h2');
          heading.focus({ preventScroll: true });
          this.scrollToQuestion(instant);
        }
        this.querySelector('[data-sell-question]').classList.remove('sf10-confirmed');
      }
      scrollToQuestion(instant = false) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const heading = this.querySelector('[data-question-heading]');
          const card = this.querySelector('.sf10-device-card');
          if (!heading || !card || !this.isConnected) return;
          const mobile = matchMedia('(max-width: 960px)').matches;
          // Measure the compact sticky card before positioning the new heading.
          if (mobile) this.classList.add('is-scrolled');
          const stickyTop = parseFloat(getComputedStyle(card).top) || 0;
          const offset = mobile ? stickyTop + card.getBoundingClientRect().height + 16 : stickyTop + 4;
          this.style.setProperty('--sf-sell-scroll-offset', `${offset}px`);
          const top = Math.max(0, heading.getBoundingClientRect().top + window.scrollY - offset);
          window.scrollTo({ top, behavior: instant || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        });
      }
      content(question) {
        const key = question.key;
        let body = '';
        if (question.type === 'brand')
          body = `<div class="sf10-answer-grid">${this.brands
            .map((brand) => {
              const popular =
                this.models.find((model) => model.brand === brand && model.popular) ||
                this.models.find((model) => model.brand === brand);
              return this.radio(
                key,
                brand,
                brand,
                popular?.image
                  ? `<img src="${escape(popular.image)}" alt="" width="150" height="150" loading="lazy">`
                  : '',
              );
            })
            .join('')}</div>${!this.brands.length ? `<p>${escape(this.t('no_models'))}</p>` : ''}`;
        if (question.type === 'model') {
          const models = this.models.filter((model) => model.brand === this.answers.brand);
          body = `<label class="sf10-model-search"><span class="visually-hidden">${escape(this.t('find_model'))}</span><input type="search" data-model-search autocomplete="off" aria-controls="SellModels-${escape(this.dataset.sectionId || 'preview')}" aria-expanded="true" placeholder="${escape(this.t('find_model'))}"></label><div class="sf10-model-list" id="SellModels-${escape(this.dataset.sectionId || 'preview')}">${models.map((model) => `<div data-model-name="${escape(model.name.toLocaleLowerCase())}">${this.radio(key, model.name, model.name, model.image ? `<img src="${escape(model.image)}" alt="" width="70" height="70" loading="lazy">` : '')}</div>`).join('')}</div><p data-model-empty hidden role="status">${escape(this.t('no_models'))}</p>`;
        }
        if (question.type === 'storage') {
          const capacities = this.model()?.storage || [];
          const maximum = Math.max(...capacities.map(Number), 1);
          body = `<div class="sf10-answer-grid sf10-storage-chips">${capacities
            .map((capacity) =>
              this.radio(
                key,
                capacity,
                Number(capacity) >= 1024 ? `${Number(capacity) / 1024} TB` : `${capacity} GB`,
                `<span class="sf10-capacity-visual" aria-hidden="true"><span style="transform:scaleX(${Math.max(0.12, Number(capacity) / maximum)})"></span></span>`,
              ),
            )
            .join('')}</div>`;
        }
        if (question.type === 'colour') {
          const colours =
            Array.isArray(this.model()?.colours) && this.model().colours.length
              ? this.model().colours
              : (this.config.defaultColours || '')
                  .split(',')
                  .map((entry) => entry.trim())
                  .filter(Boolean);
          body = `
            <fieldset>
              <legend class="visually-hidden">${escape(question.heading)}</legend>
              <div class="sf10-answer-grid sf10-colour-swatches">
                ${colours
                  .map((colour) =>
                    this.radio(
                      'colourChoice',
                      colour,
                      colour,
                      `<span class="sf10-colour-disc" style="--swatch-colour:${this.tint(colour)}" aria-hidden="true"></span>`,
                    ),
                  )
                  .join('')}
                ${this.radio('colourChoice', '__other', this.t('other_colour'), `<span class="sf10-colour-other" aria-hidden="true">${this.lineIcon('plus')}</span>`)}
              </div>
            </fieldset>
            <label class="sf9-field sf10-other-colour" ${this.answers.colourChoice === '__other' ? '' : 'hidden'}>
              ${escape(this.t('other_colour_label'))}
              <input data-sell-field="colour" maxlength="60" autocomplete="off" value="${escape(this.answers.colourChoice === '__other' ? this.answers.colour : '')}">
            </label>`;
        }
        if (question.type === 'battery') {
          const recordedPercent = /^\d+$/.test(this.answers.batteryPercent || '') ? this.answers.batteryPercent : '';
          const disabled = this.answers.batteryUnknown === 'yes' ? 'disabled' : '';
          body = `
            <div class="sf10-battery-panel">
              <div class="sf10-battery-dial" aria-hidden="true">
                <span class="sf10-battery-shell"><i data-battery-dial style="transform:scaleX(${disabled ? 0 : Number(recordedPercent) / 100 || 0})"></i></span>
                <strong data-battery-value>${disabled || !recordedPercent ? '—' : recordedPercent + '%'}</strong>
              </div>
              <p class="sf10-battery-input-hint">${escape(this.t('battery_input_hint'))}</p>
              <label class="sf10-battery-slider" ${recordedPercent ? '' : 'data-battery-unset'}>
                <span class="visually-hidden">${escape(this.t('battery'))}</span>
                <input type="range" min="0" max="100" value="${recordedPercent || 0}"
                  aria-valuetext="${escape(recordedPercent ? recordedPercent + '%' : this.t('not_selected'))}"
                  data-sell-field="batteryPercent" ${disabled}>
              </label>
              <label class="sf9-field sf10-battery-number">
                <span class="visually-hidden">${escape(this.t('battery'))}</span>
                <input type="number" min="1" max="100" inputmode="numeric" placeholder="${escape(this.t('battery_number_hint'))}"
                  value="${recordedPercent}" data-sell-field="batteryPercent" ${disabled}>
              </label>
              <div class="sf10-unknown-card">
                <label class="sf10-unknown-choice"><input type="checkbox" data-sell-field="batteryUnknown" ${disabled ? 'checked' : ''}><strong>${escape(this.t('battery_unknown'))}</strong></label>
                <button type="button" class="sf10-function-help" data-guide-button="battery" aria-label="${escape(this.t('how_check') + ': ' + this.t('battery'))}">${this.lineIcon('info')}</button>
              </div>
              ${this.model()?.batteryMode === 'status' && disabled ? `<fieldset><legend class="visually-hidden">${escape(this.t('battery_status'))}</legend><div class="sf10-answer-grid">${['normal', 'weak', 'bad', 'unknown'].map((status) => this.radio('batteryStatus', status, this.t(status))).join('')}</div></fieldset>` : ''}
            </div>`;
        }
        if (question.type === 'functions') {
          body = `
            <div class="sf10-function-grid">
              ${(this.config.checks || []).map((check) => this.checkTile(check)).join('')}
            </div>
            <p class="sf10-function-progress" data-function-progress role="status"></p>`;
        }
        if (question.type === 'faults')
          body = `<div class="sf10-fault-grid">${[
            ['spots', 'spots'],
            ['pixels', 'pixels'],
            ['lines', 'lines'],
            ['touch', 'touch'],
            ['none', 'no_issues'],
            ['unknown', 'unknown'],
          ]
            .map(
              ([value, label]) =>
                `<label class="sf10-answer-card"><input type="checkbox" data-sell-fault="${value}" ${(this.answers.screenFaults || []).includes(value) ? 'checked' : ''}>${this.conditionArt('display', 'none', value)}<span>${escape(this.t(label))}</span><i aria-hidden="true">${this.lineIcon('check')}</i></label>`,
            )
            .join('')}</div>`;
        if (question.type === 'wear')
          body = `<div class="sf10-answer-grid sf10-wear-cards">${wearValues.map((value, index) => this.radio(key, value, this.t(['no_wear', 'light_wear', 'heavy_wear', 'damaged'][index]), `${this.conditionArt(key, value)}`)).join('')}</div>`;
        if (question.type === 'location')
          body = `<div class="sf9-methods"><div class="sf9-method selected"><span aria-hidden="true">${this.lineIcon('home')}</span><div><strong>${escape(this.t('in_store'))}</strong><small>${escape(this.t('in_store_note'))}</small></div><b aria-hidden="true">${this.lineIcon('check')}</b></div><div class="sf9-method upcoming" aria-disabled="true"><span aria-hidden="true">${this.lineIcon('package')}</span><div><strong>${escape(this.t('mail_in'))}</strong><small>${escape(this.t('coming_soon'))}</small></div></div></div><div class="sf10-answer-grid">${this.locations.map((place) => this.radio(key, place.name, place.name, `<small>${escape(place.address)}</small>`)).join('')}</div>`;
        if (question.type === 'summary')
          body =
            this.ticket() +
            `${this.photoUploader()}<div class="sf9-fields"><label class="sf9-field">${escape(this.t('name'))}<input name="contact[name]" data-contact-person="name" autocomplete="name" value="${escape(this.answers.name)}" maxlength="120" required></label><label class="sf9-field">${escape(this.t('email'))}<input name="contact[email]" data-contact-person="email" type="email" autocomplete="email" value="${escape(this.answers.email)}" maxlength="254" required></label></div><label class="sf9-check sf9-consent"><input type="checkbox" name="contact[Consent]" value="Yes" required>${escape(this.t('consent'))} ${this.config.privacy ? `<a href="${escape(this.config.privacy)}">${escape(this.t('privacy'))}</a>` : ''}</label><button type="${this.dataset.preview === 'true' ? 'button' : 'submit'}" ${this.dataset.preview === 'true' ? 'data-sell-preview-submit' : ''} class="sf8-primary sf10-request"><span>${escape(this.t('submit'))}</span><i class="sf10-submit-spinner" hidden aria-hidden="true"></i></button><p data-sell-status role="status"></p>`;
        return question.type === 'colour' || question.type === 'battery' || question.type === 'summary'
          ? body
          : `<fieldset><legend class="visually-hidden">${escape(question.heading)}</legend>${body}</fieldset>`;
      }
      tint(colour) {
        const key = String(colour || '').toLocaleLowerCase();
        const map = this.config.colourTints || {};
        const tint = Object.entries(map).find(([name]) => name.toLocaleLowerCase() === key)?.[1];
        return /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(tint || '') ? tint : 'var(--sf-muted)';
      }
      checkState(check) {
        const answer = this.answers[check.key];
        if (!functionValues.includes(answer)) return 'unset';
        if (answer === 'unknown') return 'unknown';
        const negative = check.negative === true || check.positive_answer === 'no';
        return answer === (negative ? 'no' : 'yes') ? 'works' : 'issue';
      }
      checkLabel(check) {
        const state = this.checkState(check);
        const negative = check.negative === true || check.positive_answer === 'no';
        if (state === 'unset') return this.t('not_answered');
        if (state === 'unknown') return this.t('unknown');
        if (negative) return this.t(state === 'works' ? 'no_known_issues' : 'known_issues');
        return this.t(state === 'works' ? 'works' : 'does_not_work');
      }
      checkIcon(key) {
        return this.lineIcon(key);
      }
      checkTile(check) {
        const state = this.checkState(check);
        const mark = this.stateIcon(check);
        return `
          <fieldset class="sf10-function-tile" data-function-key="${escape(check.key)}" data-state="${state}">
            <legend class="visually-hidden">${escape(check.heading)}</legend>
            ${functionValues.map((value) => `<input type="radio" hidden name="sell-${escape(check.key)}" data-check-value="${value}" value="${value}" ${this.answers[check.key] === value ? 'checked' : ''}>`).join('')}
            <button type="button" class="sf10-function-cycle" data-function-cycle="${escape(check.key)}"
              aria-label="${escape(check.heading + ': ' + this.checkLabel(check) + '. ' + this.t('cycle_check'))}">
              ${this.checkIcon(check.guide || check.key)}
              <strong>${escape(check.short_label || check.heading)}</strong>
              <span data-check-label>${escape(this.checkLabel(check))}</span>
              <b data-check-mark aria-hidden="true">${mark}</b>
            </button>
            <button type="button" class="sf10-function-help" data-guide-button="${escape(check.guide || check.key)}"
              aria-label="${escape(this.t('how_check') + ': ' + check.heading)}">${this.lineIcon('info')}</button>
          </fieldset>`;
      }
      cycleCheck(button, instant) {
        const check = this.config.checks?.find((entry) => entry.key === button.dataset.functionCycle);
        if (!check) return;
        const values = check.negative || check.positive_answer === 'no' ? ['no', 'yes', 'unknown'] : functionValues;
        const current = values.indexOf(this.answers[check.key]);
        this.answers[check.key] = values[(current + 1) % values.length];
        const tile = button.closest('[data-function-key]');
        const state = this.checkState(check);
        tile.dataset.state = state;
        tile
          .querySelectorAll('[data-check-value]')
          .forEach((field) => (field.checked = field.value === this.answers[check.key]));
        tile.querySelector('[data-check-label]').textContent = this.checkLabel(check);
        tile.querySelector('[data-check-mark]').innerHTML = this.stateIcon(check);
        button.setAttribute('aria-label', check.heading + ': ' + this.checkLabel(check) + '. ' + this.t('cycle_check'));
        this.querySelector('[data-sell-validation]').textContent = '';
        this.flashPart = check.guide || check.key;
        this.updatePreview(true, instant);
        this.syncFields();
        this.save();
      }
      checkSummary(html = true) {
        const checks = (this.config.checks || []).map(
          (check) => `${check.short_label || check.heading}: ${this.checkLabel(check)}`,
        );
        return html ? checks.map(escape).join('<br>') : checks.join('; ');
      }
      ticket() {
        return `<div class="sf10-trade-ticket"><span class="sf8-kicker">${escape(this.t('assessment_label'))}</span><dl>${this.questions
          .filter((question) => question.type !== 'summary')
          .map(
            (question) =>
              `<div><dt>${escape(question.summaryLabel)}</dt><dd>${question.type === 'functions' ? this.checkSummary() : escape(this.displayValue(question))}<button type="button" data-sell-edit="${this.questions.indexOf(question)}" aria-label="${escape(this.t('edit') + ': ' + question.summaryLabel)}">${escape(this.t('edit'))}</button></dd></div>`,
          )
          .join('')}</dl></div>`;
      }
      displayValue(question) {
        if (question.key === 'battery')
          return this.answers.batteryUnknown === 'yes'
            ? this.t(this.answers.batteryStatus || 'unknown')
            : `${this.answers.batteryPercent || '—'}%`;
        if (question.key === 'storage') return this.answers.storage ? `${this.answers.storage} GB` : '—';
        if (question.key === 'screenFaults')
          return (this.answers.screenFaults || [])
            .map((value) => this.t(value === 'none' ? 'no_issues' : value))
            .join(', ');
        if (question.type === 'wear')
          return (
            this.t(
              { none: 'no_wear', light: 'light_wear', heavy: 'heavy_wear', damaged: 'damaged' }[
                this.answers[question.key]
              ],
            ) || '—'
          );
        if (question.type === 'functions') return this.checkSummary(false);
        return this.answers[question.key] || '—';
      }
      valid(question) {
        if (question.type === 'summary') return true;
        if (question.type === 'colour') return Boolean(this.answers.colour?.trim());
        if (question.key === 'brand') return this.brands.includes(this.answers.brand);
        if (question.key === 'model') return Boolean(this.model());
        if (question.key === 'storage') return this.model()?.storage.includes(this.answers.storage);
        if (question.type === 'battery')
          return this.answers.batteryUnknown === 'yes'
            ? this.model()?.batteryMode !== 'status' ||
                ['normal', 'weak', 'bad', 'unknown'].includes(this.answers.batteryStatus)
            : /^\d+$/.test(this.answers.batteryPercent || '') &&
                Number(this.answers.batteryPercent) >= 1 &&
                Number(this.answers.batteryPercent) <= 100;
        if (question.type === 'functions')
          return (this.config.checks || []).every((check) => functionValues.includes(this.answers[check.key]));
        if (question.type === 'wear') return wearValues.includes(this.answers[question.key]);
        if (question.type === 'faults') return this.answers.screenFaults?.length > 0;
        if (question.type === 'location') return this.locations.some((place) => place.name === this.answers.location);
        return false;
      }
      validate() {
        if (this.valid(this.questions[this.position])) return true;
        this.querySelector('[data-sell-validation]').textContent = this.t('answer_required');
        return false;
      }
      answer(key, value, instant = false) {
        const changed = this.answers[key] !== value;
        this.answers[key] = value;
        if (key === 'brand' && changed) {
          delete this.answers.model;
          delete this.answers.storage;
        }
        if (key === 'model' && changed) {
          delete this.answers.storage;
          delete this.answers.batteryPercent;
          delete this.answers.batteryStatus;
          delete this.answers.batteryUnknown;
          delete this.answers.colour;
          delete this.answers.colourChoice;
          if (this.model()?.batteryMode === 'status') this.answers.batteryUnknown = 'yes';
        }
        this.updatePreview(true, instant);
        this.syncFields();
        this.save();
        const question = this.questions[this.position];
        if (
          (['brand', 'model', 'storage', 'colour', 'wear', 'location'].includes(question.type) ||
            key === 'batteryStatus') &&
          this.valid(question)
        ) {
          this.querySelector('[data-sell-question]').classList.add('sf10-confirmed');
          clearTimeout(this.advanceTimer);
          this.advanceTimer = setTimeout(
            () => this.show(this.position + 1, instant),
            instant || matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200,
          );
        }
      }
      change(event) {
        const input = event.target;
        if (input.dataset.sellField) {
          if (input.dataset.sellField === 'batteryUnknown') {
            this.answers.batteryUnknown = input.checked ? 'yes' : 'no';
            this.show(this.position, event.detail === 0, false);
          } else if (input.dataset.sellField === 'colourChoice') {
            this.answers.colourChoice = input.value;
            if (input.value === '__other') {
              this.answers.colour = '';
              this.show(this.position, true, false);
              this.querySelector('.sf10-other-colour input').focus();
            } else this.answer('colour', input.value, event.detail === 0 && input.matches(':focus-visible'));
          } else
            this.answer(input.dataset.sellField, input.value, event.detail === 0 && input.matches(':focus-visible'));
        }
        if (input.dataset.sellFault) {
          const value = input.dataset.sellFault;
          let faults = new Set(this.answers.screenFaults || []);
          if (input.checked) {
            if (['none', 'unknown'].includes(value)) faults = new Set([value]);
            else {
              faults.delete('none');
              faults.delete('unknown');
              faults.add(value);
            }
          } else faults.delete(value);
          this.answers.screenFaults = [...faults];
          this.querySelectorAll('[data-sell-fault]').forEach(
            (field) => (field.checked = faults.has(field.dataset.sellFault)),
          );
          this.updatePreview(true);
          this.syncFields();
          this.save();
        }
      }
      input(event) {
        const input = event.target;
        if (input.hasAttribute('data-model-search')) {
          const query = input.value.toLocaleLowerCase().trim();
          let visible = 0;
          this.querySelectorAll('[data-model-name]').forEach((card) => {
            card.hidden = !card.dataset.modelName.includes(query);
            if (!card.hidden) visible++;
          });
          this.querySelector('[data-model-empty]').hidden = visible > 0;
        }
        if (input.dataset.contactPerson) {
          this.answers[input.dataset.contactPerson] = input.value;
          this.save();
        }
        if (input.dataset.sellField && ['text', 'number', 'range'].includes(input.type)) {
          this.answers[input.dataset.sellField] = input.value.slice(0, 60);
          if (input.dataset.sellField === 'batteryPercent') {
            this.querySelectorAll('[data-sell-field="batteryPercent"]').forEach((other) => {
              if (other !== input) other.value = input.value;
            });
            const capacity = Math.max(0, Math.min(100, Number(input.value))) / 100;
            this.querySelector('[data-battery-dial]')?.style.setProperty('transform', `scaleX(${capacity})`);
            this.querySelector('.sf10-battery-slider')?.toggleAttribute('data-battery-unset', input.value === '');
            this.querySelector('[type="range"]')?.setAttribute(
              'aria-valuetext',
              input.value ? input.value + '%' : this.t('not_selected'),
            );
            const value = this.querySelector('[data-battery-value]');
            if (value) value.textContent = input.value ? `${input.value}%` : '—';
          }
          this.updatePreview();
          this.syncFields();
          this.save();
        }
      }
      updatePreview(pop = false, instant = false) {
        const model = this.querySelector('[data-device-model]');
        if (!model) return;
        model.textContent = this.answers.model || '';
        model.hidden = !this.answers.model;
        const words = (this.answers.model || '').split(' ');
        this.querySelector('[data-device-screen-model]').textContent = words.slice(0, 2).join(' ');
        this.querySelector('[data-device-screen-model-rest]').textContent = words.slice(2).join(' ');
        this.querySelector('[data-device-storage]').textContent = this.answers.storage
          ? `${this.answers.storage} GB`
          : '—';
        const percent =
          this.answers.batteryUnknown !== 'yes' && /^\d+$/.test(this.answers.batteryPercent || '')
            ? Math.max(0, Math.min(100, Number(this.answers.batteryPercent)))
            : 0;
        this.querySelector('[data-device-battery-fill]').setAttribute('width', String(percent * 1.32));
        this.querySelector('[data-device-battery]').textContent = percent
          ? `${percent}%`
          : this.answers.batteryUnknown === 'yes' && this.answers.batteryStatus
            ? this.t(this.answers.batteryStatus)
            : '—';
        this.querySelector('.sf10-device-body').style.setProperty('--device-tint', this.tint(this.answers.colour));
        if (pop && !instant && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.previewAnimation?.cancel();
          this.previewAnimation = this.querySelector('.sf10-device-body').animate(
            [
              { translate: '0 0', scale: '1' },
              { translate: '0 -3px', scale: '1.025' },
              { translate: '0 0', scale: '1' },
            ],
            { duration: 220, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' },
          );
        }
        this.querySelector('[data-preview-wear]').dataset.wear = this.answers.display || 'none';
        const checks = this.config.checks || [];
        const yesCount = checks.filter((check) => this.checkState(check) === 'works').length;
        const issues = checks.filter((check) => this.checkState(check) === 'issue').length;
        this.querySelector('[data-device-checks]').innerHTML = checks
          .map((check, index) => {
            const state = this.checkState(check);
            const iconKey = {
              works: 'check',
              issue: check.negative ? 'warning' : 'cross',
              unknown: 'help',
              unset: 'empty',
            }[state];
            return `<g transform="translate(85 ${232 + index * 23})" data-state="${state}"><svg x="0" y="-10" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${this.lineIcon(iconKey).match(/<svg[^>]*>([\s\S]*)<\/svg>/)[1]}</svg><text x="21" y="0" class="sf12-checklist-label">${escape(check.short_label || check.heading)}</text></g>`;
          })
          .join('');
        if (this.flashPart) {
          const part = [...this.querySelectorAll('[data-device-part]')].find(
            (element) => element.dataset.devicePart === this.flashPart,
          );
          if (part) {
            const check = checks.find((entry) => (entry.guide || entry.key) === this.flashPart);
            part.dataset.state = this.checkState(check);
            this.ringAnimation?.cancel();
            if (!instant && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
              this.ringAnimation = part.animate(
                [{ opacity: 0 }, { opacity: 1, offset: 0.2 }, { opacity: 1, offset: 0.72 }, { opacity: 0 }],
                { duration: 700, easing: 'ease-out' },
              );
            }
          }
          this.flashPart = null;
        }
        const complete = this.t(yesCount === 1 ? 'checks_done_one' : 'checks_done_other').replace(
          /\{\{\s*count\s*\}\}|__COUNT__/g,
          String(yesCount),
        );
        const issueText = this.t(issues === 1 ? 'issues_one' : 'issues_other').replace(
          /\{\{\s*count\s*\}\}|__COUNT__/g,
          String(issues),
        );
        this.querySelector('[data-device-facts]').textContent = [
          this.answers.colour,
          yesCount ? complete : '',
          issues ? issueText : '',
        ]
          .filter(Boolean)
          .join(' · ');
        const progress = this.querySelector('[data-function-progress]');
        if (progress)
          progress.textContent = this.t('function_progress')
            .replace(
              /\{\{\s*count\s*\}\}|__COUNT__/g,
              String(checks.filter((check) => functionValues.includes(this.answers[check.key])).length),
            )
            .replace(/\{\{\s*total\s*\}\}|__TOTAL__/g, String(checks.length));
      }
      photoUploader() {
        if (!this.config.photoUploadUrl)
          return `<p class="sf10-photo-promise">${escape(this.config.photoInstruction)}</p><button type="button" class="sf9-help-link" data-guide-button="photos">${escape(this.t('photo_guide'))} ${this.lineIcon('arrow')}</button>`;
        this.photos = this.photos || {};
        const slots = ['display', 'frame', 'back']
          .map((key) => {
            const photo = this.photos[key];
            return `<div class="sf-photo-slot${photo ? ' has-photo' : ''}"><label><input type="file" accept="image/*" data-photo-slot="${key}" class="visually-hidden"><span class="sf-photo-thumb">${photo ? `<img src="${photo}" alt="">` : this.lineIcon('plus')}</span><span class="sf-photo-name">${escape(this.t(key + '_photo'))}</span></label>${photo ? `<button type="button" class="sf-photo-remove" data-photo-remove="${key}" aria-label="${escape(this.t('remove'))}">×</button>` : ''}</div>`;
          })
          .join('');
        return `<div class="sf-photo-upload"><p class="sf10-photo-promise">${escape(this.t('photos_heading'))}</p><div class="sf-photo-slots">${slots}</div><p class="sf-photo-hint">${escape(this.t('photos_hint'))}</p></div>`;
      }
      async addPhoto(input) {
        const file = input.files && input.files[0];
        if (!file) return;
        const data = await this.shrinkPhoto(file);
        if (!data) return;
        this.photos = this.photos || {};
        this.photos[input.dataset.photoSlot] = data;
        this.photoState = '';
        this.show(this.position, true, false);
      }
      async shrinkPhoto(file) {
        try {
          const bitmap = await createImageBitmap(file);
          const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(bitmap.width * scale);
          canvas.height = Math.round(bitmap.height * scale);
          canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
          return canvas.toDataURL('image/jpeg', 0.82);
        } catch (error) {
          if (file.size > 3000000) return null;
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          });
        }
      }
      /** Short fingerprint of a photo (data URL): a different photo in the same slot gives a different value. */
      fingerprint(data) {
        const str = String(data || '');
        let h = 2166136261;
        for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
        return str.length + '-' + (h >>> 0).toString(36);
      }
      photoLine() {
        const count = Object.keys(this.photos || {}).length;
        if (this.photoState === 'sent') return count ? `${count} sent by e-mail, reference ${this.photoRef} (also in Dispatch → To do)` : `none added — reference ${this.photoRef} (also in Dispatch → To do)`;
        if (this.photoState === 'mailfailed') return `${count} received by Dispatch but NOT e-mailed — reference ${this.photoRef}; please ask the customer to reply with the photos`;
        if (this.photoState === 'failed') return count ? 'upload failed, please ask the customer for photos' : 'none added';
        return 'none added';
      }
      syncFields() {
        const destination = this.querySelector('[data-answer-fields]');
        if (!destination) return;
        const fields = this.questions
          .filter((question) => !['summary', 'functions'].includes(question.type))
          .map((question) => [
            {
              brand: 'Brand',
              model: 'Model',
              storage: 'Storage',
              colour: 'Colour',
              battery: 'Battery',
              display: 'Display',
              frame: 'Frame',
              back: 'Back',
              screenFaults: 'Screen issues',
              location: 'Store',
            }[question.key] || question.heading,
            this.displayValue(question),
          ]);
        (this.config.checks || []).forEach((check) =>
          fields.push([check.heading, this.t(this.answers[check.key]) || '—']),
        );
        fields.push(['Method', this.t('in_store')]);
        this.kluisFields = fields.slice(); // the answers, also sent to Dispatch (Sell requests)
        if (this.config.photoUploadUrl) fields.push(['Photos', this.photoLine()]);
        const summary =
          fields.map(([name, value]) => `${name}: ${value}`).join('\n') +
          (this.config.photoUploadUrl
            ? ''
            : `\n\n${this.config.photoInstruction}\n${this.t('photo_guide')}: ${this.config.photoGuideUrl || ''}`);
        destination.innerHTML =
          fields
            .map(
              ([name, value]) =>
                `<input type="hidden" name="contact[${escape(name.replace(/[\[\]]/g, ''))}]" value="${escape(value)}">`,
            )
            .join('') + `<textarea hidden name="contact[body]">${escape(summary)}</textarea>`;
      }
      submit(event) {
        const missing = this.questions.findIndex((question) => !this.valid(question));
        if (missing >= 0) {
          event.preventDefault();
          this.show(missing, true);
          this.validate();
          return;
        }
        if (!this.form.reportValidity()) {
          event.preventDefault();
          return;
        }
        this.syncFields();
        this.save();
        if (this.dataset.preview === 'true') {
          event.preventDefault();
          this.querySelector('[data-sell-status]').textContent = this.config.previewMessage || this.t('previewMessage');
          return;
        }
        // Every request goes to Dispatch (Sell requests, answered from the staff app); photos are optional.
        // Sent again whenever the details change — including a different photo in the same slot (v9.3: the photo's
        // content counts, not only which slots are filled).
        if (this.kluisPending) {
          event.preventDefault(); // a second tap while Dispatch is still receiving the first: wait for it
          return;
        }
        const photoKeys = Object.keys(this.photos || {});
        const sendKey = JSON.stringify([this.answers.name, this.answers.email, this.kluisFields, photoKeys.map((slot) => slot + ':' + this.fingerprint(this.photos[slot]))]);
        if (this.config.photoUploadUrl && this.sentKey !== sendKey && !this.passThrough) {
          event.preventDefault();
          this.kluisPending = true;
          this.photoState = 'sending';
          const busy = this.querySelector('[type="submit"]');
          busy.setAttribute('aria-busy', 'true');
          busy.querySelector('span').textContent = this.t('sending');
          busy.querySelector('i').hidden = false;
          const device = ['brand', 'model', 'storage'].map((key) => this.answers[key]).filter(Boolean).join(' ');
          fetch(this.config.photoUploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify({
              action: 'sellRequest',
              args: {
                name: this.answers.name || '',
                email: this.answers.email || '',
                device,
                details: this.kluisFields || [],
                photos: photoKeys.map((slot) => ({ slot, data: this.photos[slot].split(',')[1] })),
              },
            }),
          })
            .then((response) => response.json())
            .then((result) => {
              if (!result || !result.ok) throw new Error((result && result.error) || 'upload');
              this.photoRef = result.data.ref;
              this.saved = true; // Dispatch has the request (v9.5: then no second e-mail via Shopify's contact form)
              this.photoState = result.data.photos === 'failed' ? 'mailfailed' : 'sent';
              this.sentKey = sendKey; // only marked as sent once Dispatch has it
            })
            .catch(() => {
              this.photoState = 'failed'; // not saved: a later submit with the same details tries Dispatch again
            })
            .finally(() => {
              this.kluisPending = false;
              this.syncFields();
              if (this.saved) return this.showSuccess(); // Dispatch saved it and e-mails the shop: done
              this.passThrough = true; // Dispatch not reachable: this submit goes to Shopify (its e-mail is the fallback)
              if (this.form.requestSubmit) this.form.requestSubmit();
              else this.form.submit();
            });
          return;
        }
        this.passThrough = false;
        this.submitting = true;
        const button = this.querySelector('[type="submit"]');
        button.setAttribute('aria-busy', 'true');
        button.querySelector('span').textContent = this.t('sending');
        button.querySelector('i').hidden = false;
        // Keep the native submit event and Shopify CAPTCHA. Only Shopify's confirmed response displays success.
      }
      renderPhotoGuides(container) {
        if (!container) return;
        container.innerHTML = ['display', 'frame', 'back']
          .map(
            (key) =>
              `<article class="sf10-photo-guide">${this.conditionArt(key)}<strong>${escape(this.t(key + '_photo'))}</strong><p>${escape(this.config.guides?.find((guide) => guide.key === key)?.text || '')}</p><button type="button" data-guide-button="${key}">${escape(this.t('photo_guide'))} ${this.lineIcon('arrow')}</button></article>`,
          )
          .join('');
      }
      /** The same thank-you screen Shopify's form shows, after Dispatch saved the request (no second e-mail). */
      showSuccess() {
        try {
          sessionStorage.removeItem(this.key);
        } catch {}
        const escape = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
        const box = document.createElement('section');
        box.className = 'sf10-sell-success';
        box.setAttribute('role', 'status');
        box.setAttribute('data-sell-success', '');
        box.id = 'sell-photo-guide';
        box.innerHTML = `<span class="sf10-success-mark" aria-hidden="true">${this.lineIcon('check')}</span>` +
          `<h2>${escape(this.config.successHeading || '')}</h2><p>${escape(this.config.success || '')}</p>` +
          `<strong>${escape(this.config.photoInstruction || '')}</strong><div class="sf10-photo-guides" data-success-guides></div>`;
        this.form.replaceChildren(box);
        this.renderPhotoGuides(box.querySelector('[data-success-guides]'));
        if (box.scrollIntoView) box.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      bindHelp(opts) {
        this.addEventListener(
          'click',
          (event) => {
            const trigger = event.target.closest('[data-guide-button]');
            if (!trigger) return;
            const key = trigger.dataset.guideButton;
            const dialog = this.querySelector('[data-sell-help]');
            if (!dialog) return;
            const guide = trigger.closest('[data-function-key]')
              ? null
              : this.config.guides?.find((entry) => entry.key === key);
            const content = dialog.querySelector('[data-sell-help-content]');
            const question = this.config.checks?.find((check) => check.key === key);
            content.innerHTML = `<h2>${escape(guide?.heading || question?.heading || this.t('photo_guide'))}</h2><p>${escape(this.model()?.batteryMode === 'status' ? guide?.samsung || guide?.text || question?.text : guide?.text || question?.text)}</p><div class="sf10-help-illustration" data-guide="${escape(key)}">${this.phone()}</div>`;
            if (key !== 'photos') {
              const value =
                key === 'battery'
                  ? this.answers.batteryUnknown === 'yes'
                    ? this.t(this.answers.batteryStatus || 'unknown')
                    : this.answers.batteryPercent
                      ? this.answers.batteryPercent + '%'
                      : '—'
                  : key === 'storage'
                    ? this.answers.storage
                      ? this.answers.storage + ' GB'
                      : '—'
                    : '';
              const target = content.querySelector('[data-device-storage]');
              if (target) target.textContent = value;
            }
            if (key === 'photos') {
              content.innerHTML = `<h2>${escape(this.t('photo_guide'))}</h2><p>${escape(this.config.photoInstruction)}</p><div data-success-guides></div>`;
              this.renderPhotoGuides(content.querySelector('[data-success-guides]'));
            }
            if (!dialog.open) dialog.showModal();
          },
          opts,
        );
        this.querySelector('[data-sell-help-close]')?.addEventListener(
          'click',
          () => this.querySelector('[data-sell-help]').close(),
          opts,
        );
        this.querySelector('[data-sell-help]')?.addEventListener(
          'click',
          (event) => {
            if (event.target === event.currentTarget) event.currentTarget.close();
          },
          opts,
        );
      }
      disconnectedCallback() {
        this.abort?.abort();
        this.previewObserver?.disconnect();
        this.previewAnimation?.cancel();
        this.ringAnimation?.cancel();
        clearTimeout(this.advanceTimer);
        cancelAnimationFrame(this.scrollFrame);
      }
    },
  );
})();
